import * as React from 'react';
import { useMemo } from 'react';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import PromoMessage from './PromoMessage/PromoMessage';
import Footer from './Footer/Footer';
import { BaggageList } from '../../BaggageList';
import { convertBaggageOnBaggageItem } from '../utils';
import { Baggage } from '../../../BaggageService/types';
import { useBaggageToggleable } from '../../hooks';
import { PassengerLabels } from '../../../Checkout/types';
import { HandlerAdditionalBaggageOnChange, SelectedAdditionalBaggage } from '../types';
import UpgradeBaggageDialog from '../../UpgradeBaggageDialog/components/UpgradeBaggageDialog';
import { joinServicesByRfics } from '../../UpgradeBaggageDialog/utils';
import { getMinPrice, getPriceToPay } from '../../../BaggagePage/utils';
import { Segment, Passenger, SelectedBaggageService } from '../../types';
import BaggageContextProvider from '../../context';
import cn from 'classnames';
import { ServiceMeta } from '../../../Checkout/store/selectedServices/actions';

interface Props {
	additionalBaggage: Baggage[];
	passengers: Passenger[];
	passengerLabels: PassengerLabels;
	segments: Segment[];
	selectedServices: SelectedAdditionalBaggage[];
	onChange?: HandlerAdditionalBaggageOnChange;
	isLoading: boolean;
	dynamicBaggagePricing: boolean;
	disabled?: boolean;
	className?: string;
	analyticsMeta?: ServiceMeta;
	onDelete?: (servicesToDelete: SelectedBaggageService[]) => void;
}

const AdditionalBaggage: React.FC<Props> = props => {
	const { AdditionalBaggage: css } = useTheme('AdditionalBaggage');
	const { t } = useTranslation('AdditionalBaggage');

	const { isOpen, open, close } = useBaggageToggleable(props.additionalBaggage);

	const baggagesByRfics = useMemo(() => joinServicesByRfics(props.additionalBaggage), [props.additionalBaggage]);

	const priceToPay = useMemo(() => getPriceToPay(props.selectedServices, props.additionalBaggage), [
		props.additionalBaggage,
		props.selectedServices
	]);

	const minPrice = useMemo(() => getMinPrice(props.additionalBaggage), [props.additionalBaggage]);

	const items = useMemo(() => {
		return baggagesByRfics
			.map(baggage => convertBaggageOnBaggageItem(baggage, props.selectedServices, props.segments))
			.sort((a, b) => a.baggage[0].value?.amount - b.baggage[0].value?.amount);
	}, [baggagesByRfics, props.selectedServices, props.segments]);

	return (
		<div className={cn(css.root, props.className)}>
			<div className={css.header}>
				<div>{t('More bags?')}</div>
				<PromoMessage className={css.promo_message} />
			</div>
			<div className={css.body}>
				<BaggageContextProvider
					value={{
						onBaggageIconClick: open,
						onBaggageDelete: props.onDelete
					}}
				>
					<BaggageList items={items} slidesToShow={2} />
				</BaggageContextProvider>
			</div>

			<div className={css.footer}>
				<Footer price={priceToPay} minPrice={minPrice} onClickEdit={open} isLoading={props.isLoading} />
			</div>

			<UpgradeBaggageDialog
				className={props.className}
				open={isOpen}
				onClose={close}
				passengers={props.passengers}
				passengerLabels={props.passengerLabels}
				segments={props.segments}
				includedBaggage={[]}
				upgradeBaggage={props.additionalBaggage}
				disabled={props.disabled}
				selectedServices={props.selectedServices}
				dynamicBaggagePricing={props.dynamicBaggagePricing}
				isLoading={props.isLoading}
				headerProps={{
					header: t('Check-in baggage'),
					description: t('More baggage for you')
				}}
				analyticsMeta={props.analyticsMeta}
				dataTestId="ExtraBaggagePopup"
			/>
		</div>
	);
};

export default AdditionalBaggage;
